import { applyFilters, financeByCurrency, ledgerTotals, reconcile, type FinanceRow, type ReconRow } from '@urbanrats/ops-core';
import { DataTable } from '../components/DataTable.tsx';
import { Link } from '../components/Link.tsx';
import { PageHead, Section } from '../components/common.tsx';
import { useEffect, useState } from 'react';
import { fmtAtomic, fmtTime as fmtTimeShort, hrefWith, useCtx } from '../lib.ts';
import { ServerFinance } from './ServerReports.tsx';

export function Finance() {
  const { data, filters } = useCtx();
  const f = applyFilters(data, filters);
  const rows = financeByCurrency(f.bets);
  const led = ledgerTotals(f.txs);
  const m = (r: FinanceRow, v: bigint) => fmtAtomic(v, r.scale, r.currency);
  const col = (key: keyof FinanceRow, header: string) => ({ key, header, numeric: true, cell: (r: FinanceRow) => <span className={(r[key] as bigint) < 0n ? 'bad-text' : ''}>{m(r, r[key] as bigint)}</span>, csv: (r: FinanceRow) => m(r, r[key] as bigint) });
  return (
    <>
      <PageHead title="Finance">
        Per currency, never summed across currencies. Cash, promo, refunds and pending are separate. GGR (proposed definition) = settled cash stake − settled cash payout; refunded stakes excluded; promo cost = freebet credits + Lightning/boost uplift on cash wins.
      </PageHead>
      <DataTable<FinanceRow>
        testId="finance-table"
        rows={rows}
        rowKey={(r) => r.currency}
        empty="No bets match these filters."
        exportName="finance"
        columns={[
          { key: 'cur', header: 'Currency', cell: (r) => <Link to={hrefWith('/bets', { ...filters, currency: r.currency })}>{r.currency}</Link>, csv: (r) => r.currency },
          col('cashStakeAccepted', 'Cash stake accepted'),
          col('settledCashStake', 'Settled cash stake'),
          col('settledCashPayout', 'Settled cash payout'),
          col('ggr', 'GGR'),
          col('promoStake', 'Promo stake (freebet face)'),
          col('promoCost', 'Promo cost'),
          col('refunds', 'Refunds'),
          col('pendingCredit', 'Pending credit'),
          col('pendingRefund', 'Pending refund'),
          col('openCashStake', 'Open cash stake'),
        ]}
      />
      <ServerFinance />
      <Section title="Ledger cross-check" testId="ledger-check">
        {rows.map((r) => {
          const checks = [
            ['debits = cash stake accepted', led.debits.get(r.currency) === r.cashStakeAccepted],
            ['credits = cash payout + freebet credits', led.credits.get(r.currency) === r.settledCashPayout + r.freebetCredits],
            ['rollbacks = refunds', led.rollbacks.get(r.currency) === r.refunds],
          ] as const;
          return (
            <p key={r.currency} data-testid={`ledger-${r.currency}`}>
              {r.currency}: {checks.map(([l, ok]) => `${ok ? '✓' : '✕'} ${l}`).join(' · ')}
            </p>
          );
        })}
        {filters.from || filters.to ? <p className="note">With a date filter, bets and ledger entries are cut by their own timestamps, so a bet and its credit can fall on different sides of the boundary.</p> : null}
      </Section>
    </>
  );
}

interface ReconCheck {
  operatorId: string;
  currency: string;
  scale: number;
  check: string;
  journal: string;
  source: string;
  difference: string;
  ok: boolean;
}
interface ReconServer {
  asOf: string;
  ok: boolean;
  checks: ReconCheck[];
  open: { bets: { state: string; count: number; oldestS: number | null }[]; wallet: { transactionId: string; operatorId: string; operation: string; state: string; betId: string | null; amountAtomic: string; currency: string; ageS: number }[] };
}

/** Plain names for the server checks (the server's own wording stays visible as the rule). */
const CHECK_TEXT: [RegExp, string, string][] = [
  [/^settled stake/, 'Stakes', 'Stakes booked in our ledger = stakes of the bets whose result was sent to the wallet.'],
  [/^settled payout/, 'Payouts', 'Wins booked in our ledger = win amounts we actually sent to the operator wallet.'],
  [/^refunds/, 'Refunds', 'Refunds booked = refunds the operator wallet confirmed for voided rounds.'],
  [/^journal balanced/, 'Ledger balanced', 'Every money movement is written twice (debit and credit); the two totals must be equal.'],
];
const BET_STATE_TEXT: Record<string, string> = {
  SUBMITTED: 'bet received, not yet sent to the wallet',
  DEBIT_PENDING: 'waiting for the wallet to take the stake',
  CREDIT_PENDING: 'waiting for the wallet to confirm the result payment',
  REFUND_PENDING: 'waiting for the wallet to confirm a refund',
  COMPENSATION_PENDING: 'late stake being returned',
};
const ageText = (s: number | null) => (s === null ? '—' : s < 90 ? `${Math.round(s)} s` : s < 5400 ? `${Math.round(s / 60)} min` : `${(s / 3600).toFixed(1)} h`);

/** Reconciliation: do our books match the bets and the operator wallets? Server checks + open items. */
export function Reconciliation() {
  const { backend } = useCtx();
  const server = backend.server;
  const [r, setR] = useState<ReconServer | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (!server) return;
    const t = setTimeout(
      () =>
        void server.get<ReconServer>('/v1/admin/reports/reconciliation').then((x) => {
          if (x.ok) setR(x.value);
          else setErr(x.error);
        }),
      0,
    );
    return () => clearTimeout(t);
  }, [server]);
  if (!server) return <ReconciliationDemo />;
  if (err)
    return (
      <p role="alert" className="field-error">
        {err}
      </p>
    );
  if (!r) return <p aria-busy="true">Checking the books…</p>;
  const groups = new Map<string, ReconCheck[]>();
  for (const c of r.checks) groups.set(`${c.operatorId}|${c.currency}`, [...(groups.get(`${c.operatorId}|${c.currency}`) ?? []), c]);
  const bad = r.checks.filter((c) => !c.ok).length;
  const openBets = r.open.bets.reduce((n, b) => n + b.count, 0);
  const unknown = r.open.wallet.filter((w) => w.state === 'UNKNOWN').length;
  const old = r.open.wallet.filter((w) => w.ageS > 300).length + r.open.bets.filter((b) => (b.oldestS ?? 0) > 900).length;
  return (
    <>
      <PageHead title="Reconciliation">Do our books match reality? We compare our ledger with the bets and with what the operator wallets confirmed — per operator and per currency, to the last cent. Checked {fmtTimeShort(r.asOf)}.</PageHead>
      <div className="au-summary">
        <div className={`au-card ${bad ? 'bad' : 'ok'}`} data-testid="recon-status">
          <b>{bad ? `✕ ${bad} difference${bad === 1 ? '' : 's'} found` : '✓ Books match'}</b>
          <span>{bad ? 'Investigate before closing the period: a difference is never rounded away.' : `All ${r.checks.length} checks equal, across ${groups.size} operator / currency pair${groups.size === 1 ? '' : 's'}.`}</span>
        </div>
        <div className={`au-card ${openBets ? 'warn' : ''}`}>
          <b>{openBets}</b>
          <span>bets still in progress (normal while rounds run)</span>
        </div>
        <div className={`au-card ${unknown ? 'bad' : ''}`}>
          <b>{unknown}</b>
          <span>wallet calls with unknown outcome (in suspense)</span>
        </div>
        <div className={`au-card ${old ? 'warn' : ''}`}>
          <b>{old}</b>
          <span>items older than expected (need a look)</span>
        </div>
      </div>

      <Section title="Checks per operator and currency" testId="recon-table">
        {groups.size === 0 && <p className="empty">No money has moved yet.</p>}
        <div className="rc-grid">
          {[...groups.entries()].map(([k, list]) => {
            const [op, cur] = k.split('|');
            const allOk = list.every((c) => c.ok);
            return (
              <div key={k} className={`rc-card ${allOk ? '' : 'bad'}`}>
                <h3>
                  {op} · {cur} <span className={`tag ${allOk ? 'dv-tag-done' : 'st-FAILED'}`}>{allOk ? 'match' : 'difference'}</span>
                </h3>
                <ul>
                  {[...list].sort((a, b) => CHECK_TEXT.findIndex(([re]) => re.test(a.check)) - CHECK_TEXT.findIndex(([re]) => re.test(b.check))).map((c) => {
                    const t = CHECK_TEXT.find(([re]) => re.test(c.check));
                    return (
                      <li key={c.check} className={c.ok ? '' : 'bad'} title={`${t?.[2] ?? ''} Rule: ${c.check}`}>
                        <span className="rc-mark">{c.ok ? '✓' : '✕'}</span>
                        <span className="rc-name">{t?.[1] ?? c.check}</span>
                        <span className="rc-val">{/^journal/.test(c.check) ? (c.ok ? 'debits = credits' : 'debits ≠ credits') : fmtAtomic(BigInt(c.journal), c.scale, c.currency)}</span>
                        {!c.ok && (
                          <span className="rc-diff">
                            source {fmtAtomic(BigInt(c.source), c.scale, c.currency)} · difference {fmtAtomic(BigInt(c.difference), c.scale, c.currency)}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
        <details>
          <summary>What each check means</summary>
          <ul className="plain">
            {CHECK_TEXT.map(([, name, text]) => (
              <li key={name}>
                <strong>{name}:</strong> {text}
              </li>
            ))}
          </ul>
        </details>
      </Section>

      <Section title="Open items" testId="recon-pending">
        <p className="note">Money movements that have not finished yet. They are expected while the game runs; we retry automatically. Old items usually mean an operator wallet is not answering.</p>
        {r.open.bets.length === 0 && r.open.wallet.length === 0 ? (
          <p className="ok-text">Nothing open.</p>
        ) : (
          <>
            {r.open.bets.length > 0 && (
              <ul className="plain">
                {r.open.bets.map((b) => (
                  <li key={b.state}>
                    <strong>{b.count}</strong> bet{b.count === 1 ? '' : 's'} {BET_STATE_TEXT[b.state] ?? b.state} · oldest {ageText(b.oldestS)} <span className="muted mono">({b.state})</span>
                  </li>
                ))}
              </ul>
            )}
            {r.open.wallet.length > 0 && (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Wallet call</th>
                      <th>Status</th>
                      <th>Operator</th>
                      <th>Age</th>
                      <th>Bet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.open.wallet.map((w) => (
                      <tr key={w.transactionId}>
                        <td>{w.operation === 'DEBIT' ? 'take stake' : w.operation === 'CREDIT' ? 'pay result' : 'refund'}</td>
                        <td>{w.state === 'UNKNOWN' ? <span className="bad-text">unknown outcome — in suspense</span> : 'waiting for answer'}</td>
                        <td className="mono">{w.operatorId}</td>
                        <td className={w.ageS > 300 ? 'bad-text' : ''}>{ageText(w.ageS)}</td>
                        <td>{w.betId ? <Link to={`/bets/${w.betId}`}>{w.betId.slice(-10)}</Link> : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Section>
    </>
  );
}

function ReconciliationDemo() {
  const { data, filters } = useCtx();
  const players = applyFilters(data, filters).players;
  const keep = new Set(players.map((p) => p.playerRef));
  const rec = reconcile({ players, bets: data.bets.filter((b) => keep.has(b.playerRef)), txs: data.txs.filter((x) => keep.has(x.playerRef)) });
  return (
    <>
      <PageHead title="Reconciliation">Balance = initial − debits + credits + rollbacks for every player wallet; every bet has the ledger entries its state requires. Pending items are listed, not counted as errors.</PageHead>
      <p className={rec.issues.length ? 'bad-text' : 'ok-text'} data-testid="recon-status">
        {rec.issues.length === 0 ? `✓ No mismatches (${rec.rows.length} wallets)` : `✕ ${rec.issues.length} issue(s)`}
      </p>
      {rec.issues.length > 0 && (
        <ul className="plain">
          {rec.issues.map((i) => (
            <li key={`${i.kind}-${i.ref}`}>
              {i.kind} {i.ref}: {i.detail}
            </li>
          ))}
        </ul>
      )}
      <Section title={`Pending (${rec.pending.length})`} testId="recon-pending">
        {rec.pending.length === 0 ? (
          <p className="muted">Nothing pending.</p>
        ) : (
          <ul className="plain">
            {rec.pending.map((p) => (
              <li key={p.ref}>
                <Link to={hrefWith(`/bets/${p.ref}`, filters)}>{p.ref}</Link> {p.state}
              </li>
            ))}
          </ul>
        )}
      </Section>
      <DataTable<ReconRow>
        testId="recon-table"
        rows={rec.rows}
        rowKey={(r) => r.playerRef}
        empty="No wallets match these filters."
        exportName="reconciliation"
        columns={[
          { key: 'p', header: 'Player', cell: (r) => r.playerRef, csv: (r) => r.playerRef },
          { key: 'i', header: 'Initial', numeric: true, cell: (r) => fmtAtomic(r.initial, r.scale, r.currency), csv: (r) => fmtAtomic(r.initial, r.scale, r.currency) },
          { key: 'd', header: 'Debits', numeric: true, cell: (r) => fmtAtomic(r.debits, r.scale, r.currency), csv: (r) => fmtAtomic(r.debits, r.scale, r.currency) },
          { key: 'c', header: 'Credits', numeric: true, cell: (r) => fmtAtomic(r.credits, r.scale, r.currency), csv: (r) => fmtAtomic(r.credits, r.scale, r.currency) },
          { key: 'r', header: 'Rollbacks', numeric: true, cell: (r) => fmtAtomic(r.rollbacks, r.scale, r.currency), csv: (r) => fmtAtomic(r.rollbacks, r.scale, r.currency) },
          { key: 'e', header: 'Expected', numeric: true, cell: (r) => fmtAtomic(r.expected, r.scale, r.currency), csv: (r) => fmtAtomic(r.expected, r.scale, r.currency) },
          { key: 'a', header: 'Ledger balance', numeric: true, cell: (r) => fmtAtomic(r.actual, r.scale, r.currency), csv: (r) => fmtAtomic(r.actual, r.scale, r.currency) },
          { key: 'ok', header: 'OK', cell: (r) => (r.ok ? '✓' : '✕'), csv: (r) => (r.ok ? 'OK' : 'MISMATCH') },
        ]}
      />
    </>
  );
}
